import { resultCrumbName } from '@/components/layout/breadcrumbs';
import { paths } from '@/routes/paths';
import type { ResultSummary } from '@/types';
import type { RecentList } from './types';

export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const diff = Date.now() - d.getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function tsOf(iso: string | null | undefined): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

export function resultHref(
  tab: 'target-list' | 'strategic-buyer' | 'fv-results',
  r: ResultSummary,
): string {
  if (tab === 'fv-results') {
    const id = r.resultId || r.processId;
    return paths.financialVerticalsResults(id);
  }
  return paths.viewResult(r.processId);
}

export function buildRecentLists(
  target: ResultSummary[] | undefined,
  strategic: ResultSummary[] | undefined,
  fv: ResultSummary[] | undefined,
  limit = 6,
): RecentList[] {
  const rows: RecentList[] = [];
  (target ?? []).forEach((r) => {
    rows.push({
      id: `t-${r.processId}`,
      name: r.resultFilename || 'Target list',
      kind: 'target',
      typeLabel: 'Targets',
      href: resultHref('target-list', r),
      ts: tsOf(r.createdAt),
      modified: timeAgo(r.createdAt),
      count: r.totalMatches ?? 0,
    });
  });
  (strategic ?? []).forEach((r) => {
    rows.push({
      id: `s-${r.processId}`,
      name: r.resultFilename || 'Strategic buyer list',
      kind: 'buyer',
      typeLabel: 'Buyers',
      href: resultHref('strategic-buyer', r),
      ts: tsOf(r.createdAt),
      modified: timeAgo(r.createdAt),
      count: r.totalMatches ?? 0,
    });
  });
  (fv ?? []).forEach((r) => {
    rows.push({
      id: `f-${r.resultId || r.processId}`,
      name:
        resultCrumbName(undefined, r.resultFilename, r.filtersApplied) ||
        r.resultFilename ||
        'Untitled result',
      kind: 'buyer',
      typeLabel: 'Buyers',
      href: resultHref('fv-results', r),
      ts: tsOf(r.createdAt),
      modified: timeAgo(r.createdAt),
      count: r.totalCount ?? r.totalMatches ?? 0,
    });
  });
  return rows.sort((a, b) => b.ts - a.ts).slice(0, limit);
}
